import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { packagesAPI } from '../services/api';
import './UatPrompt.css';

/**
 * Telling a student on the ESLCE dashboard that UAT exists.
 *
 * A CARD, always — never a full-screen modal.
 *
 * ## Why the modal went
 *
 * Each package owns its own prompt: its own component, endpoint, switches and
 * copy. Nothing about UAT should have to know that EAU exists. A modal breaks
 * that, and not in a subtle way — two packages each rendering a full-screen
 * backdrop stack, and the lower one's dismiss button cannot be reached. The
 * only fixes are for the packages to coordinate (shared state between things
 * that are meant to be independent) or for neither to use a modal.
 *
 * Cards stack fine. Two of them are a list; two modals are a bug. So the
 * novelty that a modal was carrying moves into a "New" badge, which costs a
 * student nothing to ignore and does not interrupt them.
 *
 * ## Two shapes, one card
 *
 * `held` says which conversation this is. To someone who does not own UAT it
 * is an offer, and it names the price. To someone who does, it is the door
 * back in — which is a different thing and must never look like a sales
 * prompt for the rest of their time on Lomi.
 *
 * That second shape exists because UAT shipped without it: the check was `not
 * has_access`, so the card vanished the moment a student paid, and anything
 * that returned them to /dashboard stranded them on the wrong exam with no
 * route back. The people it hit hardest were the ones who had paid.
 *
 * Renders NOTHING unless the server says to.
 */

type Prompt = {
  show: boolean;
  /** They already own it: show the way back, not the pitch. */
  held?: boolean;
  prompt: { title?: string; body?: string; cta?: string; dismiss?: string };
  amount: number | null;
  currency: string;
};

const UatPrompt: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<Prompt | null>(null);

  useEffect(() => {
    let cancelled = false;
    packagesAPI
      .uatPrompt()
      .then((d) => {
        if (!cancelled && d?.show) setData(d);
      })
      .catch(() => {
        /* a prompt that cannot load is simply not shown */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const go = useCallback(() => {
    // Straight to the package, not the storefront: they already know what it
    // is, they just read about it.
    navigate('/p/uat-aau/lessons');
  }, [navigate]);

  if (!data) return null;

  const p = data.prompt || {};
  const price = data.amount
    ? `${data.amount} ${data.currency === 'ETB' ? 'birr' : data.currency}`
    : null;

  // Owned. Not an offer — a door.
  if (data.held) {
    return (
      <button className="uatp-card uatp-card--own" onClick={go}>
        <span className="uatp-card__icon" aria-hidden="true">🎓</span>
        <span className="uatp-card__text">
          <strong>AAU UAT Prep</strong>
          <span className="uatp-card__sub">Continue where you left off</span>
        </span>
        <span className="uatp-card__arrow" aria-hidden="true">→</span>
      </button>
    );
  }

  return (
    <button className="uatp-card" onClick={go}>
      <span className="uatp-card__icon" aria-hidden="true">🎓</span>
      <span className="uatp-card__text">
        <strong>
          {p.title || 'AAU UAT Prep'}
          <span className="uatp-card__new"> ✨ New</span>
        </strong>
        <span className="uatp-card__sub">
          Free to start{price ? ` · ${price} for everything` : ''}
        </span>
      </span>
      <span className="uatp-card__arrow" aria-hidden="true">→</span>
    </button>
  );
};

export default UatPrompt;
